import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Flame, Calculator, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteFooter, SiteHeader } from "@/components/tools/ToolShell";
import { CATEGORIES, TOOLS, type ToolCategory } from "@/lib/tools-catalog";
import { ToolIcon } from "@/components/tools/ToolIcon";

export const Route = createFileRoute("/free-tools/")({
  beforeLoad: () => {
    throw redirect({ to: "/", statusCode: 301 });
  },
  head: () => ({
    meta: [
      { title: "Free Classroom Tools for Teachers – Name Pickers, Wheels & Timers | Picamini.online" },
      {
        name: "description",
        content:
          "37+ free interactive classroom tools for teachers — random name picker, spin the wheel, team generator, classroom timer, seating chart, brain breaks and games. No signup. Works on Chromebook, iPad, and smartboard.",
      },
      { name: "keywords", content: "free classroom tools for teachers, random name picker, spin the wheel classroom, team generator for class, classroom timer online, seating chart generator, brain break ideas, free teacher tools no signup" },
      { property: "og:title", content: "Free Classroom Tools for Teachers – Picamini.online" },
      { property: "og:site_name", content: "Picamini.online" },
      {
        property: "og:description",
        content:
          "37+ free classroom tools — name pickers, spinner wheels, team makers, timers and games. No signup, no download, 100% private.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/" },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Picamini.online – Free Classroom Tools for Teachers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Free Classroom Tools for Teachers – Picamini.online" },
      { name: "twitter:description", content: "37+ free tools — name pickers, spinner wheels, team makers, timers, and games for any classroom." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Picamini.online Free Classroom Tools Hub",
          "url": "https://picamini.online/",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "All",
          "inLanguage": "en",
          "browserRequirements": "Requires JavaScript. Requires HTML5.",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "featureList": [
            "Random Name Picker",
            "Spin the Wheel",
            "Wheel of Names",
            "Team Generator",
            "Classroom Timer",
            "Seating Chart Generator",
            "Dice Roller",
            "Coin Flip",
            "Brain Break Picker",
            "Classroom Scoreboard"
          ],
          "description": "37+ free interactive web tools for teachers including random name pickers, spinner wheels, team generators, classroom timers, and educational game generators. No signup required."
        })
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Free Classroom Tools",
              "item": "https://picamini.online/"
            }
          ]
        })
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Are all of these classroom tools completely free for teachers?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Every tool on Picamini.online — including name pickers, spinner wheels, team generators, and timers — is 100% free with no subscription, no registration, and no hidden charges required."
              }
            },
            {
              "@type": "Question",
              "name": "Do I need to re-enter student names every time I open the tool?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. All tools automatically save your entered names and lists locally in your browser, so your class rosters are ready every time you return without any account needed."
              }
            },
            {
              "@type": "Question",
              "name": "Can I display these classroom tools on a Smartboard or Promethean board?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Every tool features a dedicated Fullscreen mode with high-contrast text and responsive scaling optimized for interactive whiteboards, flat panel displays, and projectors."
              }
            },
            {
              "@type": "Question",
              "name": "Can I share a custom wheel with colleagues or students?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Spinner wheels include a Share Wheel button that copies a direct link pre-populated with your items, so you can share custom classroom wheels with other teachers instantly."
              }
            },
            {
              "@type": "Question",
              "name": "Do these tools work on school Chromebooks and iPads?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. All tools are lightweight HTML and JavaScript web apps built to run smoothly on low-powered school Chromebooks, Windows laptops, MacBooks, iPads, and Android tablets."
              }
            },
            {
              "@type": "Question",
              "name": "Can I link these free classroom tools into Google Classroom or Canvas LMS?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Paste direct tool links into Google Classroom assignments, Canvas LMS modules, Schoology, or ClassDojo posts for students to access immediately."
              }
            }
          ]
        })
      }
    ]
  }),
  component: Hub,
});


export function Hub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<ToolCategory | "All">("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TOOLS.filter((t) => {
      const matchesCategory = category === "All" || t.category === category;
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:py-20">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] opacity-80">
              Picamini.online Free Tools
            </p>
            <h1 className="mx-auto mt-4 max-w-3xl text-4xl font-bold sm:text-5xl">
              Free Classroom Tools, Calculators & PDF Utilities
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base opacity-90">
              Name pickers, spinner wheels, team makers and timers built for teachers. No signup, no
              clutter, and your class lists never leave the browser.
            </p>
            <div className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full bg-white/15 px-4 py-2 backdrop-blur">
              <Search className="size-4 opacity-80" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools…"
                aria-label="Search free tools"
                className="border-0 bg-transparent text-primary-foreground placeholder:text-primary-foreground/70 focus-visible:ring-0"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={category === "All" ? "default" : "outline"}
              onClick={() => setCategory("All")}
            >
              All tools
            </Button>
            {CATEGORIES.map((c) => (
              <Button
                key={c}
                size="sm"
                variant={category === c ? "default" : "outline"}
                onClick={() => setCategory(c)}
              >
                {c}
              </Button>
            ))}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((tool) => (
              <Link
                key={tool.slug}
                to="/free-tools/$slug"
                params={{ slug: tool.slug }}
                className="surface-card group flex flex-col gap-3 p-5 transition-all hover:-translate-y-1 hover:shadow-lift"
              >
                <div className="flex items-center justify-between">
                  <span className="grid size-11 place-items-center rounded-xl bg-secondary text-primary transition-colors group-hover:bg-hero-gradient group-hover:text-primary-foreground">
                    <ToolIcon name={tool.icon} className="size-5" />
                  </span>
                  {tool.featured ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 shadow-xs">
                      <Flame className="size-3 text-amber-500 fill-amber-500/20" /> Popular
                    </span>
                  ) : null}
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold">{tool.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{tool.tagline}</p>
                </div>
                <span className="mt-auto text-sm font-medium text-primary">Open tool →</span>
              </Link>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="mt-12 text-center text-muted-foreground">
              No tools match “{query}”. Try another search.
            </p>
          ) : null}
        </section>

        {/* ============ Online Calculators Promotion Section ============ */}
        <section className="border-t border-border/60 bg-gradient-to-br from-primary/5 via-background to-primary/5 py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl border border-primary/20 bg-card p-6 sm:p-8 shadow-lg">
              <div className="flex-1 space-y-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
                  <Calculator className="size-3.5" /> 122+ Free Calculators
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                  Free Online Calculators
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl">
                  Instant math, finance, health, science, and education calculators — all free, all private, all browser-based. From percentage and BMI calculators to mortgage and GPA tools.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Link to="/calculators/percentage-calculator" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    Percentage Calculator
                  </Link>
                  <Link to="/calculators/bmi-calculator" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    BMI Calculator
                  </Link>
                  <Link to="/calculators/grade-calculator" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    Grade Calculator
                  </Link>
                  <Link to="/calculators/mortgage-calculator" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    Mortgage Calculator
                  </Link>
                  <Link to="/calculators/calorie-calculator" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    Calorie Calculator
                  </Link>
                </div>
              </div>
              <Link
                to="/calculators"
                className="shrink-0 inline-flex items-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-md hover:bg-primary/90 transition-colors"
              >
                <Calculator className="size-4" />
                Explore All Calculators
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ============ SEO Rich Informational Content Section ============ */}
        <section className="border-t border-border/60 bg-secondary/20 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              {/* Main H2 Header */}
              <header className="space-y-3 text-center sm:text-left">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Free Teacher Resources & Digital Classroom Utilities
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  The Complete Hub of Free Online Classroom Tools for Teachers
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Managing a modern classroom requires quick, fair, and engaging solutions. Whether you are choosing students for reading sessions, grouping teams for group projects, running timed quizzes, or energizing the class with brain breaks, Picamini.online offers <strong>37+ free interactive classroom tools</strong> designed specifically for K-12 teachers, educators, and presenters. No account registration, no downloads, and zero server data tracking.
                </p>
              </header>

              {/* Tool Category 1: Random Pickers */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  1. Random Name Pickers & Decision Selectors
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Eliminate bias and ensure every student gets an equal chance to participate. Our selection tools allow you to paste your class roster once and instantly pick individual names, letters, numbers, or answers with exciting visual animations.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/$slug" params={{ slug: "random-name-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Name Picker
                  </Link>
                  <Link to="/$slug" params={{ slug: "random-student-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Student Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-letter-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Letter Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-word-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Word Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-choice-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Choice Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "yes-or-no-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Yes or No Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-color-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Colour Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-question-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Question Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-number-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👉</span> Random Number Generator
                  </Link>
                </div>
              </div>

              {/* Tool Category 2: Spinner Wheels */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  2. Interactive Spinner Wheels for Classroom Fun & Rewards
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Bring high-energy excitement to smartboards and projectors. Spin colourful wheels to pick student winners, distribute classroom prizes, decide on lunch options, or let fate answer quick classroom questions.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/free-tools/$slug" params={{ slug: "spin-the-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎡</span> Spin the Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "wheel-of-names" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎡</span> Wheel of Names
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "yes-or-no-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎡</span> Yes or No Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "prize-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎁</span> Prize Wheel Spinner
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "lucky-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🌟</span> Lucky Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "number-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔢</span> Random Number Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "food-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🍕</span> What to Eat Food Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "activity-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🧭</span> Activity Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "student-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎓</span> Student Wheel
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "decision-wheel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔀</span> Decision Wheel
                  </Link>
                </div>
              </div>

              {/* Tool Category 3: Team & Group Makers */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  3. Instant Team Generators, Group Makers & Line Up Tools
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Stop spending precious lesson minutes manually sorting groups. Automatically divide your classroom into balanced teams, random study pairs, or generate fair line-up orders for recess and lunch.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                  <Link to="/free-tools/$slug" params={{ slug: "team-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👥</span> Team Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "group-maker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>👥</span> Group Maker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "partner-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🤝</span> Partner Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-line-order" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔢</span> Random Line Order
                  </Link>
                </div>
              </div>

              {/* Tool Category 4: Classroom Management */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  4. Essential Classroom Management & Organization Tools
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Keep lessons running smoothly with digital timers, customizable seating charts, scoreboard point trackers, and classroom job assigners.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/free-tools/$slug" params={{ slug: "classroom-timer" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>⏱️</span> Classroom Timer
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "seating-chart" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🪑</span> Classroom Seating Chart
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "classroom-helper-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>✨</span> Classroom Helper Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "classroom-job-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📋</span> Classroom Job Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "classroom-scoreboard" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🏆</span> Classroom Scoreboard
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "activity-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>💡</span> Activity Picker
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "brain-break-picker" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>💨</span> Brain Break Picker
                  </Link>
                </div>
              </div>

              {/* Tool Category 5: Educational Games */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  5. Educational Games, Warm-Ups & Activity Generators
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Spice up lesson warm-ups or end-of-day wrap-ups with quick interactive games, virtual dice rolls, trivia questions, charades words, and Would You Rather debate starters.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/free-tools/$slug" params={{ slug: "dice-roller" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎲</span> Dice Roller
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "coin-flip" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🪙</span> Coin Flip
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "charades-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎭</span> Charades Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "pictionary-word-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🎨</span> Pictionary Word Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "would-you-rather-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>⚖️</span> Would You Rather Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "trivia-question-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🧠</span> Trivia Question Generator
                  </Link>
                  <Link to="/free-tools/$slug" params={{ slug: "random-challenge-generator" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔥</span> Random Challenge Generator
                  </Link>
                </div>
              </div>

              {/* Benefits Section */}
              <div className="space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Why Teachers Choose Picamini.online Free Tools
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-base">🔒 100% Private & In-Browser</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Your student names and rosters stay safely stored in your browser’s local cache. No data is ever uploaded to remote servers.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-base">⚡ Instant Launch, No Sign-up</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Zero login barriers. Open any tool and use it immediately during active lessons without ads or paywalls.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-base">🖥️ Smartboard & Projector Ready</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Designed with big high-contrast displays, fullscreen mode, and engaging sound effects for whole-class presentation.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-base">📱 Mobile & Tablet Responsive</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Works seamlessly across desktop PCs, Chromebooks, iPads, tablets, and smartphones.
                    </p>
                  </div>
                </div>
              </div>

              {/* Practical Strategies Section */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 space-y-6">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Grade-Level Classroom Strategies with Picamini.online Tools
                </h3>

                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-bold text-foreground text-base">🌱 Primary & Elementary School (K-5)</h4>
                    <p className="mt-1 leading-relaxed">
                      Use visual spinner wheels such as the <Link to="/free-tools/$slug" params={{ slug: "prize-wheel" }} className="text-primary underline font-semibold">Prize Wheel Spinner</Link> and <Link to="/free-tools/$slug" params={{ slug: "food-wheel" }} className="text-primary underline font-semibold">Food Wheel</Link> for positive behavior reinforcement. Assign daily helper roles using the <Link to="/free-tools/$slug" params={{ slug: "classroom-job-picker" }} className="text-primary underline font-semibold">Classroom Job Picker</Link>, and energize students between subjects with quick movements from the <Link to="/free-tools/$slug" params={{ slug: "brain-break-picker" }} className="text-primary underline font-semibold">Brain Break Picker</Link>.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-base">📘 Middle School & Junior High (6-8)</h4>
                    <p className="mt-1 leading-relaxed">
                      Fairness is critical for middle school engagement. Use the <Link to="/free-tools/$slug" params={{ slug: "team-generator" }} className="text-primary underline font-semibold">Team Generator</Link> and <Link to="/free-tools/$slug" params={{ slug: "partner-generator" }} className="text-primary underline font-semibold">Partner Generator</Link> to eliminate social exclusion during lab work. Spark lively debates with the <Link to="/free-tools/$slug" params={{ slug: "would-you-rather-generator" }} className="text-primary underline font-semibold">Would You Rather Generator</Link> and track team scores on the <Link to="/free-tools/$slug" params={{ slug: "classroom-scoreboard" }} className="text-primary underline font-semibold">Classroom Scoreboard</Link>.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-base">🎓 High School & Higher Education (9-12+)</h4>
                    <p className="mt-1 leading-relaxed">
                      Maintain lesson pace and accountability with the projector-friendly <Link to="/free-tools/$slug" params={{ slug: "classroom-timer" }} className="text-primary underline font-semibold">Classroom Timer</Link>. Randomize presentation order with the <Link to="/free-tools/$slug" params={{ slug: "random-line-order" }} className="text-primary underline font-semibold">Random Line Order</Link> tool, and conduct quick review quizzes using the <Link to="/free-tools/$slug" params={{ slug: "trivia-question-generator" }} className="text-primary underline font-semibold">Trivia Question Generator</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step-by-Step Daily Workflow Section */}
              <div className="space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Step-by-Step Daily Workflow for Teachers
                </h3>
                <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside leading-relaxed">
                  <li className="pl-2">
                    <strong className="text-foreground">Morning Check-In & Job Setup:</strong> Open the <Link to="/free-tools/$slug" params={{ slug: "classroom-helper-picker" }} className="text-primary underline font-semibold">Classroom Helper Picker</Link> on your front board to assign line leaders, board erasers, and tech helpers for the day.
                  </li>
                  <li className="pl-2">
                    <strong className="text-foreground">Direct Instruction & Cold-Calling:</strong> Use the <Link to="/free-tools/$slug" params={{ slug: "wheel-of-names" }} className="text-primary underline font-semibold">Wheel of Names</Link> or <Link to="/free-tools/$slug" params={{ slug: "random-student-picker" }} className="text-primary underline font-semibold">Random Student Picker</Link> to call on students fairly during discussion.
                  </li>
                  <li className="pl-2">
                    <strong className="text-foreground">Timed Small-Group Collaboration:</strong> Divide the class into teams in one click with the <Link to="/free-tools/$slug" params={{ slug: "group-maker" }} className="text-primary underline font-semibold">Group Maker</Link> and start the countdown on the <Link to="/free-tools/$slug" params={{ slug: "classroom-timer" }} className="text-primary underline font-semibold">Classroom Timer</Link>.
                  </li>
                  <li className="pl-2">
                    <strong className="text-foreground">Lesson Wrap-Up & Reflection:</strong> Spin the <Link to="/free-tools/$slug" params={{ slug: "activity-wheel" }} className="text-primary underline font-semibold">Activity Wheel</Link> or draw a prompt from the <Link to="/free-tools/$slug" params={{ slug: "random-question-picker" }} className="text-primary underline font-semibold">Random Question Picker</Link> to summarize what was learned before the bell rings.
                  </li>
                </ol>
              </div>

              {/* Pedagogy & Research-Backed Benefits Section */}
              <div className="space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  The Pedagogy of Randomization: Why Digital Classroom Tools Work
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Educational research, including Dylan Wiliam's work on formative assessment, highlights the power of <em>"no hands up" random calling</em>. When students know anyone can be picked at any time, attentive listening increases across the entire room. Digital tools transform cold-calling from a stressful moment into an exciting, gamified experience.
                </p>
                <div className="grid gap-4 sm:grid-cols-3 text-sm">
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">⚖️ Eliminates Cognitive Bias</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Teachers naturally tend to call on students seated in the front row or those who raise their hands first. Tools like <Link to="/free-tools/$slug" params={{ slug: "random-name-picker" }} className="text-primary underline">Random Name Picker</Link> guarantee true mathematical randomness.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">🧠 Reduces Speaking Anxiety</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Animations and sound effects on tools like <Link to="/free-tools/$slug" params={{ slug: "spin-the-wheel" }} className="text-primary underline">Spin the Wheel</Link> shift focus away from individual pressure onto the fun visual outcome.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">⏱️ Maximizes Instructional Time</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Sorting 30 students into 6 groups manually can waste 5-10 minutes. <Link to="/free-tools/$slug" params={{ slug: "team-generator" }} className="text-primary underline">Team Generator</Link> accomplishes it in under 2 seconds.
                    </p>
                  </div>
                </div>
              </div>

              {/* Subject-Specific Applications */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 space-y-6">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Subject-Specific Applications across the Curriculum
                </h3>
                <div className="grid gap-6 sm:grid-cols-2 text-sm text-muted-foreground">
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">🔬 STEM & Mathematics</h4>
                    <p className="leading-relaxed">
                      Conduct probability experiments using <Link to="/free-tools/$slug" params={{ slug: "dice-roller" }} className="text-primary underline font-semibold">Dice Roller</Link> and <Link to="/free-tools/$slug" params={{ slug: "coin-flip" }} className="text-primary underline font-semibold">Coin Flip</Link>. Generate mental arithmetic challenges with <Link to="/free-tools/$slug" params={{ slug: "random-number-generator" }} className="text-primary underline font-semibold">Random Number Generator</Link> and spin numerical ranges on the <Link to="/free-tools/$slug" params={{ slug: "number-wheel" }} className="text-primary underline font-semibold">Random Number Wheel</Link>.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">📚 English & Language Arts (ELA)</h4>
                    <p className="leading-relaxed">
                      Practice phonics and spelling using <Link to="/free-tools/$slug" params={{ slug: "random-letter-picker" }} className="text-primary underline font-semibold">Random Letter Picker</Link> and <Link to="/free-tools/$slug" params={{ slug: "random-word-picker" }} className="text-primary underline font-semibold">Random Word Picker</Link>. Inspire creative storytelling or vocabulary drawing with <Link to="/free-tools/$slug" params={{ slug: "pictionary-word-generator" }} className="text-primary underline font-semibold">Pictionary Word Generator</Link>.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">🌎 Social Studies & Foreign Languages</h4>
                    <p className="leading-relaxed">
                      Promote conversational fluency with <Link to="/free-tools/$slug" params={{ slug: "would-you-rather-generator" }} className="text-primary underline font-semibold">Would You Rather Generator</Link> and prompt critical thinking using the <Link to="/free-tools/$slug" params={{ slug: "random-question-picker" }} className="text-primary underline font-semibold">Random Question Picker</Link>.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">🎨 Arts, Physical Education & Electives</h4>
                    <p className="leading-relaxed">
                      Run timed station rotations with <Link to="/free-tools/$slug" params={{ slug: "classroom-timer" }} className="text-primary underline font-semibold">Classroom Timer</Link>, gamify acting prompts with <Link to="/free-tools/$slug" params={{ slug: "charades-generator" }} className="text-primary underline font-semibold">Charades Generator</Link>, and assign random colors using <Link to="/free-tools/$slug" params={{ slug: "random-color-picker" }} className="text-primary underline font-semibold">Random Colour Picker</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Privacy & Compliance */}
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8 space-y-3">
                <h3 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
                  <span>🛡️</span> FERPA & COPPA Compliant Data Privacy by Design
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  School district IT administrators and data privacy officers love Picamini.online tools because <strong>no student Personally Identifiable Information (PII) ever touches a server</strong>. All name lists, rosters, and custom wheel options remain 100% client-side inside your local browser memory (localStorage). Closing or clearing browser cookies resets local data with zero cloud backups required.
                </p>
              </div>

              {/* Extended FAQ Section */}
              <div className="space-y-4 pt-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Frequently Asked Questions (FAQ)
                </h3>
                <div className="space-y-3 text-sm">
                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Are all of these tools completely free for teachers?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes! Every tool in our hub — including name pickers, spinner wheels, team generators, and timers — is 100% free with no subscription required.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Do I need to re-enter student names every time?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      No. All tools automatically save your entered names or lists locally in your browser, so your class rosters are ready every time you return.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Can I display these tools on Smartboards, Promethean, and SMART Boards?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Absolutely! Every tool features a dedicated <strong>Fullscreen</strong> mode with high-contrast text and responsive scaling optimized for interactive whiteboards, interactive flat panels, and projectors.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Can I share custom wheels with colleagues or students?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes! Our spinner wheels include a "Share Wheel" button that copies a direct link pre-populated with your items, allowing you to share custom wheels with other teachers.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Do these tools work on school Chromebooks and iPads?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes. All tools are lightweight HTML/JS web applications built to render smoothly on low-powered school Chromebooks, Windows laptops, MacBooks, iPads, and Android tablets.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Can I embed these free tools into Google Classroom or Canvas LMS?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      You can paste direct links to any tool or shared wheel directly into Google Classroom assignments, Canvas LMS modules, Schoology, or ClassDojo posts for students to access.
                    </p>
                  </details>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
